import { Button, Stack, Typography } from '@mui/material';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { useTranslations } from '@/i18n/use-translations';
import { SessionsTabActions } from './tab-actions';
import { Add } from '@mui/icons-material';
import { useEffect, useState, useMemo, useCallback } from 'react';
import AddSessionModal from '@/components/events/detail/tabs/sessions/tab-actions/add-session.modal.tsx';
import { SessionsTable } from '@/components/events/detail/tabs/sessions/sessions-table/sessions-table.tsx';
import { SessionsGrid } from '@/components/events/detail/tabs/sessions/sessions-grid/sessions-grid.tsx';
import { usePreferencesStore } from '@/state/stores/use-preferences-store';
import type { SessionType } from '@/types/dtos/sessions/session-type.enum';
import { useEventFilteredSessionsQuery } from '@/state/queries/sessions/use-filtered-event-sessions';
import { useEventStore } from '@/state/stores/use-event-store';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import EmptyState from '@/components/common/empty-state';
import { getObjectId } from '@/helpers/object.id.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';

export const SessionsTab = () => {
  const isDesktop = useBreakpointUp('lg');
  const [addSessionsOpen, setAddSessionsOpen] = useState(false);
  const { sessionExpandedView, setPreferences } = usePreferencesStore();
  const mode = useEventMode();
  const setSelectedSlot = useFlowStore((s) => s.setSelectedSlot);
  const setFlow = useFlowStore((s) => s.setFlow);

  const eventId = useEventStore((s) => (s.event?.origin ? getObjectId(s.event.origin) : s.event?.id));

  const [sessionType, setSessionType] = useState<SessionType | undefined>(undefined);
  const [previousSessionType, setPreviousSessionType] = useState<SessionType | undefined>(undefined);

  const {
    data: fetchedSessions,
    isLoading,
    error,
  } = useEventFilteredSessionsQuery({ eventId, type: sessionType, isInLibrary: true });
  const t = useTranslations();

  const sessions = useMemo(() => {
    if (!fetchedSessions?.pages?.length) return [];

    return fetchedSessions.pages[0].data.sort((a, b) => a.title.localeCompare(b.title)) ?? [];
  }, [fetchedSessions]);

  // Sync previousSessionType when sessionType changes (render-time update)
  const [prevSessionTypeState, setPrevSessionTypeState] = useState<SessionType | undefined>(undefined);
  const [prevSessionExpandedView, setPrevSessionExpandedView] = useState(sessionExpandedView);

  if (
    sessions.length > 0 &&
    (prevSessionTypeState !== sessionType || prevSessionExpandedView !== sessionExpandedView)
  ) {
    if (sessionType !== previousSessionType && sessionExpandedView) {
      setPreviousSessionType(sessionType);
    } else if (previousSessionType === undefined && sessionExpandedView) {
      setPreviousSessionType(sessionType);
    }
    setPrevSessionTypeState(sessionType);
    setPrevSessionExpandedView(sessionExpandedView);
  }

  const handleSetSessionType = useCallback<React.Dispatch<React.SetStateAction<SessionType | undefined>>>(
    (newSessionType) => {
      setSessionType(newSessionType);
    },
    [],
  );

  useEffect(() => {
    if (!isDesktop) {
      setPreferences({ sessionExpandedView: true });
    }
  }, [isDesktop, setPreferences]);

  useEffect(() => {
    // Leaving flow detail should clear flow slot context for session lists.
    setSelectedSlot(undefined);
    setFlow(undefined);
  }, [setFlow, setSelectedSlot]);

  const handleViewToggle = useCallback(() => {
    setPreferences({ sessionExpandedView: !sessionExpandedView });
  }, [sessionExpandedView, setPreferences]);

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} sx={{ height: 22 }}>
        <SessionsTabActions
          isExpandedView={sessionExpandedView}
          onViewButtonClick={handleViewToggle}
          setSessionType={handleSetSessionType}
          sessionType={sessionType}
        />
        <>
          <Button
            color="info"
            variant="contained"
            disabled={isOrganizeMode(mode)}
            data-testid="add-event-session-button"
            onClick={() => setAddSessionsOpen(true)}
            startIcon={<Add />}
          >
            {t('common.new')}
          </Button>
          <AddSessionModal open={addSessionsOpen} setOpen={setAddSessionsOpen} />
        </>
      </Stack>
      {isLoading || error ? (
        <>
          {isLoading && <Typography variant="body1">{t('session.loading')}</Typography>}
          {error && <Typography variant="body1">{t('session.errorLoading')}</Typography>}
        </>
      ) : (
        <>
          {sessions?.length ? (
            sessionExpandedView ? (
              <SessionsGrid sessions={sessions} />
            ) : (
              <SessionsTable sessions={sessions} />
            )
          ) : (
            <EmptyState text={t('session.noSessions')} variant="compact" />
          )}
        </>
      )}
    </Stack>
  );
};
